import React, { useState, useEffect } from "react";
import { ContentChatHeaderControls } from "@/components/content-chat/ContentChatHeaderControls";
import { ChatMessages } from "@/components/chat/chat-messages/ChatMessages";
import { ChatInputBar } from "@/components/chat/chat-input/ChatInputBar";
import { ContentChatWelcome } from "@/components/content-chat/ContentChatWelcome";
import { useContentChatSession } from "@/hooks/useContentChatSession";
import { updateChatMessage } from "@/lib/chat/updateChatMessage";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2 } from "lucide-react";
import type { ChatTabId } from "@/components/chat/ChatRoot";
import type { ContentItem } from "@/types/content";
import { UsageLimitExceededError } from "@/hooks/useUsageLimits";
import { useUsageLimitContext } from "@/context/UsageLimitContext";
import { useLanguage } from "@/context/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import type { Plan } from "@/lib/ai/models";

interface ContentChatPanelProps {
  isMobile?: boolean;
  content: ContentItem;
  onSwitchTab?: (tab: ChatTabId) => void;
}

export const ContentChatPanel: React.FC<ContentChatPanelProps> = ({
  isMobile,
  content,
  onSwitchTab,
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  // Get limit error context
  let showLimitError: ((error: UsageLimitExceededError) => void) | null = null;
  let setShowPricingDialog: ((show: boolean) => void) | null = null;
  try {
    const context = useUsageLimitContext();
    showLimitError = context.showLimitError;
    setShowPricingDialog = context.setShowPricingDialog;
  } catch {
    // Context not available
  }

  // Use the content-aware chat session hook
  const { messages, isStreaming, sendMessage, isLoadingContent, hasContentContext, clearMessages, loadConversation } =
    useContentChatSession({
      chatId: content.id,
      contentId: content.id,
      contentType: content.type,
    });

  const [replyToText, setReplyToText] = useState<string | null>(null);
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [selectedModel, setSelectedModel] = useState("auto");
  const [userPlan, setUserPlan] = useState<Plan>("free");

  // Fetch subscription status
  useEffect(() => {
    if (!user) return;
    const fetchPlan = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("subscription_status")
        .eq("id", user.id)
        .single();
      if (data?.subscription_status === "active") {
        setUserPlan("pro");
      }
    };
    fetchPlan();
  }, [user]);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isStreaming]);

  const handleSendMessage = async (text: string, attachments: File[]) => {
    try {
      await sendMessage(text, { searchEnabled: isSearchEnabled, model: selectedModel });
    } catch (error) {
      if (error instanceof UsageLimitExceededError) {
        if (showLimitError) {
          showLimitError(error);
        } else {
          toast.error("You've reached your daily chat limit. Upgrade to continue.");
        }
        return;
      }
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    }
  };

  const handleEditMessage = async (messageId: string, newText: string) => {
    if (!user) {
      toast.error("Unable to edit message");
      return;
    }

    try {
      await updateChatMessage({
        messageId,
        newMessage: newText,
      });

      toast.success("Message updated - refresh to see AI's new response");
    } catch (error) {
      console.error("Error editing message:", error);
      toast.error("Failed to edit message");
    }
  };

  const handleExampleClick = (question: string) => {
    handleSendMessage(question, []);
  };

  const handleSetReplyText = (text: string) => {
    setReplyToText(text);
  };

  const handleUpgradeRequest = () => {
    setShowPricingDialog?.(true);
  };

  // Show loading state while content text is being fetched
  if (isLoadingContent) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 px-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading content...</p>
      </div>
    );
  }

  return (
    <div
      className={[
        "relative flex w-full flex-col px-0 h-full",
        isMobile ? "p-4" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* Top-right controls */}
      <div className="absolute right-0 top-0 z-40 -mt-1 pr-2 sm:right-2 md:right-4 lg:right-8">
        <ContentChatHeaderControls
          contentId={content.id}
          onNewChat={clearMessages}
          onSelectConversation={loadConversation}
        />
      </div>

      {/* Messages or Welcome Screen */}
      <ScrollArea className="flex-1 min-h-0">
        <div data-chat-container="true" className="h-full">
          {messages.length === 0 ? (
            <ContentChatWelcome 
              onExampleClick={handleExampleClick} 
              contentTitle={content.title}
            />
          ) : (
            <div className="mx-auto mt-10 flex w-full max-w-3xl flex-grow flex-col justify-start px-4 sm:px-4">
              <ChatMessages
                messages={messages}
                isAITyping={isStreaming}
                chatId={content.id}
                onEditMessage={handleEditMessage}
                onSendMessage={(text) => handleSendMessage(text, [])}
                onSetReplyText={handleSetReplyText}
                onSwitchTab={onSwitchTab}
              />
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input Bar */}
      <div className="flex-shrink-0 mx-auto w-full max-w-3xl px-2 sm:px-0">
        <ChatInputBar
          onSendMessage={handleSendMessage}
          disabled={isStreaming}
          replyToText={replyToText}
          onClearReply={() => setReplyToText(null)}
          placeholder={t("contentChat.inputPlaceholder")}
          showAttachments={false}
          onSearchStateChange={setIsSearchEnabled}
          selectedModel={selectedModel}
          onModelChange={setSelectedModel}
          userPlan={userPlan}
          onUpgradeRequest={handleUpgradeRequest}
        />
      </div>
    </div>
  );
};
